import { mocked } from '@/configs';
import { Country } from '@/constructs/Country';
import ShippingMethod from '@/constructs/ShippingMethod';
import type { CartModel, ICart } from '@/services/models/Cart';
import { ShippingMethodModel } from '@/services/models/ShippingMethod';
import MOCK_SHIPPING_METHODS from '@/services/models/ShippingMethod/mocks/MOCK_SHIPPING_METHODS';
import type { Constructor } from '@/type-utils';
import { InvalidStateError } from '@/utils/errors';
import { isNullOrEmpty } from '@/utils/null-utils';
import { isPOBoxLine } from '@/utils/string-utils';
import { IAddress } from '@/services/models/Address';
import MockService, { MockState, ServiceMock } from '../MockService';
import type { ShippingMethodService } from './ShippingMethodService';
import { ShippingMethodNotFoundError } from './errors';
import I18NService from '../I18NService';
import { EnvironmentService } from '../EnvironmentService';

const initialState = {};

/**
 * Mock for {@link ShippingMethodService}.
 */
export default class ShippingMethodServiceMock extends ServiceMock<ShippingMethodService> {
  protected _state;

  /** @inheritdoc */
  public get state(): MockState {
    return this._state;
  }

  /** @inheritdoc */
  public getMock(): ShippingMethodService {
    return MockService.getMockOf(this.service) as ShippingMethodService;
  }

  /**
   * The constructor to initialize the mocks.
   *  @param service - The service for this mock, used to remove a circular dependency.
   */
  public constructor(private service: Constructor<ShippingMethodService>) {
    super();
    this._state = new MockState(initialState);
    this.initializeMockedMembers(this.service);
  }

  /** @inheritdoc */
  protected initializeMockedMembers(
    service: Constructor<ShippingMethodService>
  ): void {
    const mockEnabled: boolean = mocked.ShippingMethodService;

    MockService.mockService(
      mockEnabled,
      service,
      {
        getShippingMethodByUID: async (uid: string) => {
          const foundMethod = MOCK_SHIPPING_METHODS.find(
            (method) => method.uid === uid
          );

          if (!foundMethod) {
            throw new ShippingMethodNotFoundError(
              `Shipping method "${uid}" was not found.`
            );
          }

          return Promise.resolve(ShippingMethodModel.from(foundMethod));
        },

        getShippingMethodsByCountry: async (country: Country = Country.US) => {
            const currentBrand = ('AHNU');
          // Filter by country and override the brand to match current environment
          const filteredMethods = MOCK_SHIPPING_METHODS.filter(
            (shippingMethod) => shippingMethod.country === country
          ).map((method) => ({
            ...method,
            brand: currentBrand // Override mock brand with current brand
          }));

          return Promise.resolve(
            filteredMethods.map((method) => ShippingMethodModel.from(method))
          );
        },

        getApplicableShippingMethodsForCart: async (_cart: CartModel) => {
          // Return the default set of methods
          const applicableMethodIDs = [
            ShippingMethod.ST2,
            ShippingMethod.STO,
            ShippingMethod.STG
          ];

          const applicableMethods = MOCK_SHIPPING_METHODS.filter((method) =>
            applicableMethodIDs.includes(method.id as ShippingMethod)
          );

          return Promise.resolve(
            applicableMethods.map((method) => ShippingMethodModel.from(method))
          );
        },

        getBestShippingMethod: (_methods: Array<ShippingMethodModel>) => {
          const stgMethod = MOCK_SHIPPING_METHODS.find(
            (method) => (method.id as ShippingMethod) === ShippingMethod.STG
          );

          if (!stgMethod) {
            throw new InvalidStateError(
              `Shipping method "${ShippingMethod.STG}" does not exist in` +
                ' the current mock, but it is required.'
            );
          }

          return ShippingMethodModel.from(stgMethod);
        },

        isAddressPoBox: (address: IAddress): boolean => {
          const { addressLine1, addressLine2 } = address;

          if (isPOBoxLine(addressLine1)) {
            return true;
          }

          if (!isNullOrEmpty(addressLine2) && isPOBoxLine(addressLine2)) {
            return true;
          }

          return false;
        },

        isAddressWithinCurrentCountry: (address: IAddress): boolean => {
          const currentLocale = I18NService.currentLocale.country;
          return address.country === currentLocale;
        },

        cartHasAllItemsDiscounted: (cart: CartModel): boolean => {
          // If there are no items, return `false`.
          // This is to avoid having STG2 on first render and then changing to STG.
          if (cart.items.length === 0) {
            return false;
          }

          for (const lineItem of cart.items) {
            // If there is at least one item at full price...
            if (lineItem.netTotal.isGreaterThanOrEqualTo(lineItem.subtotal)) {
              return false;
            }
          }

          return true;
        }
      },
      {},
      this.state
    );
  }
}
